import { ref, type Ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import { withFrappeLoading } from "@/services/frappe/list";
import { GetCashierProfiles } from "@/api/posProfileApi";
import { Controllers, FrappeMethodType } from "@/type/api";
import type {
  CashierListItem,
  CashierRuntimeDefaults,
  CashierSettlementPayload,
  CashierSettlementSnapshot,
} from "@/type/posProfile";
import { localToday } from "@/utils/localDate";

const cashierService = new HttpClient(Controllers.account);

const GET_CASHIER_SETTLEMENT_SNAPSHOT_METHOD = "pet_app.api.accounting.cashier.get_cashier_settlement_snapshot";
const GET_CASHIER_RUNTIME_DEFAULTS_METHOD = "pet_app.api.accounting.cashier.get_cashier_runtime_defaults";
const SETTLE_CASHIER_TO_TREASURY_METHOD = "pet_app.api.accounting.cashier.settle_cashier_to_treasury";

function getToday() {
  return localToday();
}

function normalizeString(value: unknown) {
  const normalized = String(value ?? "").trim();
  return normalized || null;
}

function toNumber(value: unknown) {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function unwrapMethodPayload<T>(response: any): T {
  return (response?.message ?? response?.data ?? response ?? null) as T;
}

function mapSettlementSnapshot(
  payload: any,
  fallbackProfileName: string,
  settlementDate: string,
): CashierSettlementSnapshot {
  return {
    profileName: normalizeString(payload?.profile_name ?? payload?.profileName) || fallbackProfileName,
    company: normalizeString(payload?.company),
    warehouse: normalizeString(payload?.warehouse),
    cashAccount: normalizeString(payload?.cash_account ?? payload?.cashAccount),
    treasuryCashAccount: normalizeString(payload?.treasury_cash_account ?? payload?.treasuryCashAccount),
    defaultModeOfPayment: normalizeString(
      payload?.default_mode_of_payment
      ?? payload?.default_cash_mode_of_payment
      ?? payload?.defaultModeOfPayment,
    ),
    currentBalance: toNumber(payload?.current_balance ?? payload?.currentBalance),
    todayIncoming: toNumber(payload?.today_incoming ?? payload?.todayIncoming),
    todayOutgoing: toNumber(payload?.today_outgoing ?? payload?.todayOutgoing),
    expectedCashOnHand: toNumber(payload?.expected_cash_on_hand ?? payload?.expectedCashOnHand),
    settlementDate,
  };
}

function normalizeStringList(value: unknown) {
  if (!Array.isArray(value))
    return [];

  return value
    .map(item => String(item ?? "").trim())
    .filter(Boolean);
}

function mapRuntimeDefaults(payload: any, fallbackProfileName: string): CashierRuntimeDefaults {
  return {
    profileName: normalizeString(payload?.profile_name ?? payload?.profileName) || fallbackProfileName,
    company: normalizeString(payload?.company),
    warehouse: normalizeString(payload?.warehouse),
    cashAccount: normalizeString(payload?.cash_account ?? payload?.cashAccount),
    treasuryCashAccount: normalizeString(payload?.treasury_cash_account ?? payload?.treasuryCashAccount),
    sellingPriceList: normalizeString(
      payload?.selling_price_list
      ?? payload?.sellingPriceList
      ?? payload?.price_list
      ?? payload?.priceList,
    ),
    priceListCurrency: normalizeString(
      payload?.price_list_currency
      ?? payload?.priceListCurrency
      ?? payload?.currency,
    ),
    currency: normalizeString(payload?.currency),
    defaultModeOfPayment: normalizeString(
      payload?.default_mode_of_payment
      ?? payload?.default_cash_mode_of_payment
      ?? payload?.defaultModeOfPayment,
    ),
    allowedModeOfPayments: normalizeStringList(
      payload?.allowed_mode_of_payments ?? payload?.allowedModeOfPayments,
    ),
  };
}

export async function GetCashiers(
  loading: Ref<boolean>,
): Promise<CashierListItem[]> {
  return withFrappeLoading(loading, async () => {
    const cashiers = await GetCashierProfiles();

    const snapshots = await Promise.allSettled(
      cashiers.map(cashier => GetCashierSettlementSnapshot(ref(false), cashier.name)),
    );

    return cashiers.map((cashier, index) => ({
      ...cashier,
      ...(snapshots[index]?.status === "fulfilled" && snapshots[index].value
        ? {
            cashAccount: snapshots[index].value?.cashAccount ?? cashier.cashAccount ?? null,
            company: snapshots[index].value?.company ?? cashier.company ?? null,
            warehouse: snapshots[index].value?.warehouse ?? cashier.warehouse ?? null,
            defaultModeOfPayment: snapshots[index].value?.defaultModeOfPayment ?? cashier.defaultModeOfPayment ?? null,
          }
        : {}),
      currentBalance: Number(
        snapshots[index]?.status === "fulfilled"
          ? (
              snapshots[index].value?.currentBalance
              ?? cashier.cashAccountBalance
              ?? 0
            )
          : cashier.cashAccountBalance ?? 0,
      ),
      todayIncoming: Number(
        snapshots[index]?.status === "fulfilled"
          ? snapshots[index].value?.todayIncoming ?? 0
          : 0,
      ),
      todayOutgoing: Number(
        snapshots[index]?.status === "fulfilled"
          ? snapshots[index].value?.todayOutgoing ?? 0
          : 0,
      ),
      expectedCashOnHand: Number(
        snapshots[index]?.status === "fulfilled"
          ? (
              snapshots[index].value?.expectedCashOnHand
              ?? snapshots[index].value?.currentBalance
              ?? cashier.cashAccountBalance
              ?? 0
            )
          : cashier.cashAccountBalance ?? 0,
      ),
    }));
  });
}

export async function GetCashierSettlementSnapshot(
  loading: Ref<boolean>,
  profileName: string,
  settlementDate: string = getToday(),
): Promise<CashierSettlementSnapshot | null> {
  return withFrappeLoading(loading, async () => {
    const payload = await cashierService.get<any>(
      GET_CASHIER_SETTLEMENT_SNAPSHOT_METHOD,
      {},
      {
        profile: profileName,
        profile_name: profileName,
        posting_date: settlementDate,
      },
      undefined,
      undefined,
      FrappeMethodType.method,
    );

    const normalizedPayload = unwrapMethodPayload<any>(payload);
    if (!normalizedPayload)
      return null;

    return mapSettlementSnapshot(normalizedPayload, profileName, settlementDate);
  });
}

export async function GetCashierRuntimeDefaults(
  loading: Ref<boolean>,
  profileName: string,
): Promise<CashierRuntimeDefaults | null> {
  return withFrappeLoading(loading, async () => {
    const normalizedProfileName = normalizeString(profileName);
    if (!normalizedProfileName)
      return null;

    const payload = await cashierService.get<any>(
      GET_CASHIER_RUNTIME_DEFAULTS_METHOD,
      {},
      {
        profile: normalizedProfileName,
        profile_name: normalizedProfileName,
      },
      undefined,
      undefined,
      FrappeMethodType.method,
    );

    const normalizedPayload = unwrapMethodPayload<any>(payload);
    if (!normalizedPayload)
      return null;

    return mapRuntimeDefaults(normalizedPayload, normalizedProfileName);
  });
}

export async function CreateCashierSettlementTransfer(
  loading: Ref<boolean>,
  payload: CashierSettlementPayload,
): Promise<{ id: string | null } | null> {
  return withFrappeLoading(loading, async () => {
    const amount = Number(payload.amount ?? 0);
    if (amount <= 0)
      throw new Error("Settlement amount must be greater than 0.");

    const response = await cashierService.post<any>(
      {
        profile: payload.profileName,
        profile_name: payload.profileName,
        posting_date: payload.postingDate,
        amount,
        remarks: payload.remarks || null,
      },
      SETTLE_CASHIER_TO_TREASURY_METHOD,
      {},
      undefined,
      FrappeMethodType.method,
    );

    const normalizedPayload = unwrapMethodPayload<any>(response);
    const id = normalizeString(
      normalizedPayload?.payment_entry
      ?? normalizedPayload?.payment_entry_name
      ?? normalizedPayload?.name
      ?? normalizedPayload?.id,
    );

    return {
      id,
    };
  });
}
